import { chain, compact, groupBy, maxBy } from 'lodash';
import { DateTime } from 'luxon';

import { advancementStatuses } from '@shared/constants';
import advancementStatus from '@shared/utils/advancementStatus';

import type { YouthAdvancementItem } from './types';

type AdvancementGroup = 'started' | 'completed' | 'approved';

export type AdvancementGroups = Record<
  AdvancementGroup,
  YouthAdvancementItem[]
>;

/** Which tile an advancement counts toward, or `none` when it counts toward no tile. */
function tileGroup(item: YouthAdvancementItem): AdvancementGroup | 'none' {
  // Approved includes awarded: the tiles have no fourth group for it
  if (advancementStatus.isAtLeastApproved(item)) return 'approved';

  if (
    advancementStatus.isCompleted(item) ||
    advancementStatus.isCounselorApproved(item)
  ) {
    return 'completed';
  }

  return advancementStatus.getStatus(item) === advancementStatuses.STARTED
    ? 'started'
    : 'none';
}

/**
 * The three counts the card's tiles show, applying the same rules as the youth
 * profile's `startedItemsSel` / `completedItemsSel` / `approvedItemsSel`.
 *
 * Status is derived via `advancementStatus.getStatus`, not read off
 * `item.status`, which the API is not dependable about.
 */
export function groupAdvancementsByStatus(
  items: YouthAdvancementItem[] = [],
): AdvancementGroups {
  // Untouched advancements land under `none` and are dropped here
  const groups = groupBy(items, tileGroup);

  return {
    started: groups.started ?? [],
    completed: groups.completed ?? [],
    approved: groups.approved ?? [],
  };
}

/** Every date that counts as activity, newest wins. */
function auditDates(item: YouthAdvancementItem) {
  return [
    // Merit badges complete into `dateCompleted`, awards and ranks into `dateEarned`
    item.dateCompleted ?? item.dateEarned,
    item.leaderApprovedDate,
    item.awardedDate,
    item.dateStarted,
  ];
}

/**
 * When the youth last moved on this advancement: the newest of its completion,
 * leader-approval, awarded and start dates. `undefined` when none is usable,
 * which is how an untouched advancement drops out of the recent list.
 */
export function lastUpdatedAt(
  item: YouthAdvancementItem,
): DateTime | undefined {
  const parsed = compact(auditDates(item))
    .map(date => DateTime.fromISO(date))
    .filter(date => date.isValid);

  return maxBy(parsed, date => date.toMillis());
}

/**
 * Advancements touched within the last `days`, newest first.
 *
 * Unlike the youth profile's card this considers every item, not only completed
 * and approved ones, so a badge started last week shows with its progress bar.
 */
export function recentlyUpdated(
  items: YouthAdvancementItem[] = [],
  days = 30,
): YouthAdvancementItem[] {
  const cutoff = DateTime.now().minus({ days });

  return chain(items)
    .map(item => ({ item, updatedAt: lastUpdatedAt(item) }))
    .filter(entry => !!entry.updatedAt && entry.updatedAt >= cutoff)
    .orderBy(entry => entry.updatedAt?.toMillis() ?? 0, 'desc')
    .map('item')
    .value();
}
